import { Flex, Text } from '@chakra-ui/react';
import { ButtonText, Select, Table, useToast } from '@morressier/alembic';
import { QueryKey, useQuery } from '@tanstack/react-query';
import React from 'react';
import { useManageSubmissionAssignment } from 'src/api/commands';
import { fetchSubmissionContextRoleAssignments } from 'src/api/queries';
import ListView from 'src/layouts/ListView';
import Loading from 'src/pages/Status/Loading';
import { useSubmissionData } from '../../../contexts/SubmissionDataProvider';
import { IGetSubmissionEditorialAssigneesResponse } from '@publishing-engine/submission-service/src/endpoints/api.response-types';

interface Props {
  queryKey: QueryKey;
  editorialAssignees: IGetSubmissionEditorialAssigneesResponse['editorialAssignees'];
}

const RoleAssociationTab: React.FC<Props> = ({ editorialAssignees }) => {
  const toast = useToast();
  const {
    workflowResponse,
    refetchSubmission,
    isTaskCompleter,
    organizationId
  } = useSubmissionData();
  const { submissionId, submissionContextId } = workflowResponse;

  const { data, isLoading } = useQuery({
    queryKey: ['ctx-roles-and-users', { submissionContextId }],
    queryFn: () => fetchSubmissionContextRoleAssignments(submissionContextId),
    retry: 0
  });

  const manageSubmissionAssignmentMutation = useManageSubmissionAssignment({
    submissionContextId,
    submissionId,
    organizationId,
    subtype: 'editorial',
    invalidateSubmissionQueries: refetchSubmission
  });

  const onAssignSubmission = async (assigneeId: string, role: string) => {
    const res = await manageSubmissionAssignmentMutation.mutateAsync({
      action: 'assign',
      assigneeId,
      role
    });

    if (res.isSuccess) {
      const assignedUser = data
        ?.find(r => r.role === role)
        ?.users.find(u => u.id === assigneeId);

      const assignedUserName = assignedUser
        ? `${assignedUser.firstName} ${assignedUser.lastName}`
        : 'User';

      return toast({
        variant: 'success',
        title: `${assignedUserName} assigned to Submission`,
        isClosable: true
      });
    }

    return toast({
      variant: 'error',
      title: res.message,
      isClosable: true
    });
  };

  const onUnassignSubmission = async (assigneeId?: string, role?: string) => {
    if (!assigneeId || !role) return;

    const res = await manageSubmissionAssignmentMutation.mutateAsync({
      action: 'unassign',
      role
    });

    if (res.isSuccess) {
      const unassignedUser = data
        ?.find(r => r.role === role)
        ?.users.find(u => u.id === assigneeId);

      const unassignedUserName = unassignedUser
        ? `${unassignedUser.firstName} ${unassignedUser.lastName}`
        : 'User';
      return toast({
        variant: 'success',
        title: `${unassignedUserName} Unassigned as ${role}`,
        isClosable: true
      });
    }

    return toast({
      variant: 'error',
      title: res.message,
      isClosable: true
    });
  };

  if (isLoading) return <Loading />;
  if (!data) return 'No roles defined';

  return (
    <Flex flexDirection="column" gap={8}>
      <ListView.Body>
        <Table>
          <Table.HeaderRow>
            <Table.HeaderCell flex="4">Role</Table.HeaderCell>
            <Table.HeaderCell flex="6">Assigned User</Table.HeaderCell>
            <Table.HeaderCell flex="1" />
          </Table.HeaderRow>
          <Table.Body>
            {data
              .sort((a, b) => a.role.localeCompare(b.role))
              .map(({ role, users }) => {
                const assigneeId = editorialAssignees.find(
                  user => user.role === role
                )?.userId;

                return (
                  <Table.Row
                    spacing="md"
                    key={`${submissionId}-${role}`}
                    gap={2}
                    _last={{ borderBottom: 'none' }}
                  >
                    <Table.RowCell flex="4" textStyle="headingLine">
                      <Text textStyle="headingItem">{role}</Text>
                    </Table.RowCell>
                    <Table.RowCell flex="6" textStyle="headingLine">
                      <Select
                        key={role}
                        data-testId="assign-to-dropdown"
                        onChange={({ target: { value } }) => {
                          onAssignSubmission(value, role);
                        }}
                        isDisabled={!isTaskCompleter}
                      >
                        <option selected={!assigneeId} disabled>
                          --
                        </option>
                        {users.map(user => (
                          <option
                            key={user.id}
                            value={user.id}
                            selected={user.id === assigneeId}
                          >
                            {user.firstName} {user.lastName}
                          </option>
                        ))}
                      </Select>
                    </Table.RowCell>
                    <Table.RowCell
                      flex="1"
                      minWidth="fit-content"
                      textStyle="headingLine"
                    >
                      <ButtonText
                        data-testId="unassign-button"
                        onClick={() => onUnassignSubmission(assigneeId, role)}
                        isDisabled={!assigneeId || !isTaskCompleter}
                      >
                        Unassign
                      </ButtonText>
                    </Table.RowCell>
                  </Table.Row>
                );
              })}
          </Table.Body>
        </Table>
      </ListView.Body>
    </Flex>
  );
};

export default RoleAssociationTab;
